import React, { FormEvent, useEffect, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { Classable, HasChildren, Shapeable } from '@shapeable/types';
import { breakpoints, theme } from '@shapeable/theme';
import { classNames } from '@shapeable/utils';
import { DOING_CHALK, DOING_DEEP_BLUE } from '../../theme';

const cls = classNames('mailchimp-town-team-embed');

const mailchimpAction = 'https://doingco.us17.list-manage.com/subscribe/post?u=5b4a8d5f8fe9252de9ef732dc&id=ad1a595dee&f_id=00b3f6e3f0';
const mailchimpJsonpUrl = mailchimpAction.replace('/post?u=', '/post-json?u=');
const honeypotFieldName = 'b_5b4a8d5f8fe9252de9ef732dc_ad1a595dee';
const jsonpTimeoutMs = 15000;

type MailchimpResponse = {
  result: 'success' | 'error';
  msg: string;
};

type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error';

const invalidEmailMessage = 'This email address looks invalid. Please enter a real email address and try again.';

const townTeamChoices = [
  'Join a Town Team if one exists in my area',
  'My existing group would like to become a Town Team',
  'I would like to form a new Town Team',
];

const charterValue = 'I have read the Town Team Charter and agree that the Town Team named above will align and operate within its guiding principles.';

function formatMailchimpMessage(msg: string): string {
  if (msg === 'captcha') {
    return 'Please try again in a moment.';
  }

  const plain = msg.split('<')[0].trim();
  return plain.replace(/^\d+\s*-\s*/, '') || plain;
}

function getJsonpErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message.includes('timed out')) {
    return 'The request timed out. Please try again.';
  }

  return invalidEmailMessage;
}

function getFormFields(form: HTMLFormElement): Record<string, string> {
  const fields: Record<string, string> = {};

  new FormData(form).forEach((value, key) => {
    if (typeof value === 'string' && key !== 'subscribe') {
      fields[key] = value;
    }
  });

  return fields;
}

function submitToMailchimp(fields: Record<string, string>): Promise<MailchimpResponse> {
  return new Promise((resolve, reject) => {
    const callback = `_mailchimp_town_team_${Date.now()}`;
    const params = new URLSearchParams({ ...fields, c: callback });
    const url = `${mailchimpJsonpUrl}&${params.toString()}`;

    const script = document.createElement('script');
    let timeoutId = 0;
    let loadCheckId = 0;
    let settled = false;

    const cleanup = () => {
      window.clearTimeout(timeoutId);
      window.clearTimeout(loadCheckId);
      delete (window as unknown as Record<string, unknown>)[callback];
      script.remove();
    };

    const finish = (handler: () => void) => {
      if (settled) {
        return;
      }

      settled = true;
      cleanup();
      handler();
    };

    (window as unknown as Record<string, (response: MailchimpResponse) => void>)[callback] = (response) => {
      finish(() => resolve(response));
    };

    script.onerror = () => {
      finish(() => reject(new Error('jsonp-error')));
    };

    script.onload = () => {
      loadCheckId = window.setTimeout(() => {
        finish(() => reject(new Error('jsonp-error')));
      }, 500);
    };

    timeoutId = window.setTimeout(() => {
      finish(() => reject(new Error('timed out')));
    }, jsonpTimeoutMs);

    script.src = url;
    document.body.appendChild(script);
  });
}

export type MailchimpTownTeamEmbedProps = Classable & HasChildren;

type ContainerProps = {};

const contentIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(12px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const ContainerStyles = breakpoints({
  base: css`
    animation: ${contentIn} 1s cubic-bezier(0.16, 1, 0.3, 1) 40ms both;
    background: ${theme.COLOR('light')};
    border-radius: ${theme.UNIT(6)};
    display: grid;
    gap: ${theme.UNIT(8)};
    margin: 0 auto;
    max-width: 52rem;
    padding: ${theme.UNIT(8)};
    will-change: transform, opacity;
  `,
  desktop: css`
    padding: ${theme.UNIT(12)};
  `,
});

const IntroStyles = breakpoints({
  base: css`
    display: grid;
    gap: ${theme.UNIT(2)};
  `,
});

const TitleStyles = breakpoints({
  base: css`
    color: ${theme.COLOR('dark')};
    font-family: ${theme.FONT('heading')};
    font-size: 2rem;
    line-height: 1.05;
    margin: 0;
  `,
});

const DescriptionStyles = breakpoints({
  base: css`
    color: ${theme.COLOR('mid')};
    font-size: 1rem;
    line-height: 1.6;
    margin: 0;
  `,
});

const FormStyles = breakpoints({
  base: css`
    display: grid;
    gap: ${theme.UNIT(5)};
  `,
});

const RowStyles = breakpoints({
  base: css`
    display: grid;
    gap: ${theme.UNIT(4)};
  `,
  desktop: css`
    grid-template-columns: repeat(2, minmax(0, 1fr));
  `,
});

const FieldStyles = breakpoints({
  base: css`
    display: grid;
    gap: ${theme.UNIT(2)};
  `,
});

const LabelStyles = breakpoints({
  base: css`
    color: ${theme.COLOR('dark')};
    font-size: 0.95rem;
    font-weight: 700;
  `,
});

const InputStyles = breakpoints({
  base: css`
    appearance: none;
    background: ${DOING_CHALK};
    border: 1px solid ${theme.COLOR('appChrome')};
    border-radius: ${theme.UNIT(3)};
    color: ${DOING_DEEP_BLUE};
    color-scheme: light;
    font: inherit;
    min-height: ${theme.UNIT(12)};
    padding: ${theme.UNIT(3)} ${theme.UNIT(4)};
    -webkit-text-fill-color: ${DOING_DEEP_BLUE};

    &::placeholder {
      color: rgba(15, 36, 80, 0.65);
    }

    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus {
      -webkit-box-shadow: 0 0 0 1000px ${DOING_CHALK} inset;
      -webkit-text-fill-color: ${DOING_DEEP_BLUE};
      box-shadow: 0 0 0 1000px ${DOING_CHALK} inset;
    }

    &:focus {
      border-color: ${theme.COLOR('primary')};
      outline: 2px solid ${theme.COLOR('primary')};
      outline-offset: 2px;
    }
  `,
});

const FieldsetStyles = breakpoints({
  base: css`
    border: 0;
    display: grid;
    gap: ${theme.UNIT(3)};
    margin: 0;
    padding: 0;
  `,
});

const LegendStyles = breakpoints({
  base: css`
    color: ${theme.COLOR('dark')};
    font-size: 1rem;
    font-weight: 700;
    margin: 0;
    padding: 0;
    padding-bottom: ${theme.UNIT(2)};
  `,
});

const ChoiceStyles = breakpoints({
  base: css`
    align-items: flex-start;
    background: ${DOING_CHALK};
    border-radius: ${theme.UNIT(3)};
    cursor: pointer;
    display: grid;
    gap: ${theme.UNIT(3)};
    grid-template-columns: auto 1fr;
    padding: ${theme.UNIT(4)};
  `,
});

const RadioStyles = breakpoints({
  base: css`
    accent-color: ${theme.COLOR('primary')};
    height: 1.1rem;
    margin: 0.2rem 0 0;
    width: 1.1rem;
  `,
});

const ChoiceLabelStyles = breakpoints({
  base: css`
    color: ${DOING_DEEP_BLUE};
    line-height: 1.5;
  `,
});

const SubmitStyles = breakpoints({
  base: css`
    appearance: none;
    background: ${theme.COLOR('primary')};
    border: 0;
    border-radius: 999px;
    color: ${theme.COLOR('light')};
    cursor: pointer;
    font: inherit;
    font-weight: 700;
    justify-self: start;
    min-height: ${theme.UNIT(12)};
    padding: ${theme.UNIT(3)} ${theme.UNIT(6)};
    transition: transform 160ms ease, opacity 160ms ease;

    &:hover:not(:disabled) {
      opacity: 0.92;
      transform: translateY(-1px);
    }

    &:focus {
      outline: 2px solid ${theme.COLOR('primary')};
      outline-offset: 2px;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }
  `,
});

const SuccessResponseStyles = breakpoints({
  base: css`
    background: ${DOING_CHALK};
    border: 1px solid ${theme.COLOR('primary')};
    border-radius: ${theme.UNIT(3)};
    color: ${DOING_DEEP_BLUE};
    font-size: 0.95rem;
    line-height: 1.5;
    margin: 0;
    padding: ${theme.UNIT(4)};
  `,
});

const ErrorResponseStyles = breakpoints({
  base: css`
    background: ${DOING_CHALK};
    border: 1px solid ${theme.COLOR('red')};
    border-radius: ${theme.UNIT(3)};
    color: ${theme.COLOR('red')};
    font-size: 0.95rem;
    line-height: 1.5;
    margin: 0;
    padding: ${theme.UNIT(4)};
  `,
});

const HoneypotStyles = breakpoints({
  base: css`
    left: -5000px;
    position: absolute;
  `,
});

const My = {
  Container: styled.div<ContainerProps>`${ContainerStyles}`,
  Intro: styled.div`${IntroStyles}`,
  Title: styled.h2`${TitleStyles}`,
  Description: styled.p`${DescriptionStyles}`,
  Form: styled.form`${FormStyles}`,
  Row: styled.div`${RowStyles}`,
  Field: styled.div`${FieldStyles}`,
  Label: styled.label`${LabelStyles}`,
  Input: styled.input`${InputStyles}`,
  Fieldset: styled.fieldset`${FieldsetStyles}`,
  Legend: styled.legend`${LegendStyles}`,
  Choice: styled.label`${ChoiceStyles}`,
  Radio: styled.input`${RadioStyles}`,
  ChoiceLabel: styled.span`${ChoiceLabelStyles}`,
  Submit: styled.button`${SubmitStyles}`,
  Honeypot: styled.div`${HoneypotStyles}`,
  SuccessResponse: styled.p`${SuccessResponseStyles}`,
  ErrorResponse: styled.p`${ErrorResponseStyles}`,
};

export const MailchimpTownTeamEmbed: Shapeable.FC<MailchimpTownTeamEmbedProps> = (props) => {
  const { className } = props;
  const formRef = useRef<HTMLFormElement>(null);
  const [isReady, setIsReady] = useState(false);
  const [status, setStatus] = useState<SubmitStatus>('idle');
  const [message, setMessage] = useState('');

  useEffect(() => {
    setIsReady(true);
  }, []);

  const submitForm = async () => {
    const form = formRef.current;
    if (!form) {
      return;
    }

    if (!form.reportValidity()) {
      setStatus('error');
      setMessage('Please complete all required fields and try again.');
      return;
    }

    const fields = getFormFields(form);

    setStatus('submitting');
    setMessage('');

    try {
      const response = await submitToMailchimp(fields);

      if (response.result === 'success') {
        setStatus('success');
        setMessage(formatMailchimpMessage(response.msg));
        return;
      }

      setStatus('error');
      setMessage(formatMailchimpMessage(response.msg));
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[MailchimpTownTeamEmbed] JSONP submission failed', error);
      }

      setStatus('error');
      setMessage(getJsonpErrorMessage(error));
    }
  };

  const onFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    submitForm();
  };

  return (
    <My.Container className={cls.name(className)}>
      <My.Intro>
        <My.Title>Register a Town Team</My.Title>
        <My.Description>
          Tell us about your Town Team and how you&apos;d like to get involved.
        </My.Description>
      </My.Intro>

      {status === 'success' ? (
        <My.SuccessResponse role="status">{message}</My.SuccessResponse>
      ) : (
      <My.Form
        ref={formRef}
        id="doing-co-mailchimp-town-team-form"
        onSubmit={onFormSubmit}
      >
        <My.Field>
          <My.Label htmlFor="mce-town-team-EMAIL">Email Address *</My.Label>
          <My.Input id="mce-town-team-EMAIL" name="EMAIL" required type="email" />
        </My.Field>

        <My.Field>
          <My.Label htmlFor="mce-town-team-MMERGE4">Town Team or organisation name</My.Label>
          <My.Input id="mce-town-team-MMERGE4" name="MMERGE4" type="text" />
        </My.Field>

        <My.Row>
          <My.Field>
            <My.Label htmlFor="mce-town-team-TTFNAME">Town Team contact first name</My.Label>
            <My.Input id="mce-town-team-TTFNAME" name="TTFNAME" type="text" />
          </My.Field>

          <My.Field>
            <My.Label htmlFor="mce-town-team-TTSNAME">Town Team contact surname</My.Label>
            <My.Input id="mce-town-team-TTSNAME" name="TTSNAME" type="text" />
          </My.Field>
        </My.Row>

        <My.Row>
          <My.Field>
            <My.Label htmlFor="mce-town-team-TT_PH">Town Team phone</My.Label>
            <My.Input id="mce-town-team-TT_PH" inputMode="numeric" name="TT_PH" type="tel" />
          </My.Field>

          <My.Field>
            <My.Label htmlFor="mce-town-team-TTEMAIL">Town Team email</My.Label>
            <My.Input id="mce-town-team-TTEMAIL" name="TTEMAIL" type="email" />
          </My.Field>
        </My.Row>

        <My.Field>
          <My.Label htmlFor="mce-town-team-TTWEBSITE">Town Team website</My.Label>
          <My.Input id="mce-town-team-TTWEBSITE" name="TTWEBSITE" type="url" />
        </My.Field>

        <My.Fieldset>
          <My.Legend>Town Team choices</My.Legend>
          {townTeamChoices.map((option, index) => {
            const id = `mce-town-team-MMERGE6-${index}`;

            return (
              <My.Choice htmlFor={id} key={option}>
                <My.Radio id={id} name="MMERGE6" type="radio" value={option} />
                <My.ChoiceLabel>{option}</My.ChoiceLabel>
              </My.Choice>
            );
          })}
        </My.Fieldset>

        <My.Fieldset>
          <My.Legend>Town Team charter</My.Legend>
          <My.Choice htmlFor="mce-town-team-MMERGE14-0">
            <My.Radio
              id="mce-town-team-MMERGE14-0"
              name="MMERGE14"
              type="radio"
              value={charterValue}
            />
            <My.ChoiceLabel>{charterValue}</My.ChoiceLabel>
          </My.Choice>
        </My.Fieldset>

        <My.Honeypot aria-hidden="true">
          <input autoComplete="new-password" defaultValue="" name={honeypotFieldName} tabIndex={-1} type="text" />
        </My.Honeypot>

        {status === 'error' && (
          <My.ErrorResponse role="alert">{message}</My.ErrorResponse>
        )}

        <My.Submit
          disabled={!isReady || status === 'submitting'}
          id="doing-co-mailchimp-town-team-submit"
          onClick={submitForm}
          type="button"
        >
          {status === 'submitting' ? 'Subscribing…' : 'Subscribe'}
        </My.Submit>
      </My.Form>
      )}
    </My.Container>
  )
};

MailchimpTownTeamEmbed.cls = cls;
